import { Box, Button, Flex, FormControl, FormErrorMessage, FormLabel, Input } from '@chakra-ui/react';

import { AsyncSelect, Select } from 'chakra-react-select';
import { Form, Formik, useFormik } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PhoneInput, { parsePhoneNumber } from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import countryList from 'react-select-country-list';
import { urlNavigate } from '../../helper/utils/navigation.js';
import { data as cityOptions } from '../../mock/city.json';
import { doHandleState } from '../../redux/waiver/waiver.actions.js';
import FormLayoutProvider from '../layout/FormLayoutProvider.jsx';
import { WaiverRegistration } from './hooks/validation.js';

export default function UserLogin() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params, _] = useSearchParams();

    const { data = {} } = useSelector((s) => s?.waiver);

    const isEdit = params.get('isEdit') || false;
    const userId = params.get('id') || '';

    const handleBack = () => {
        navigate(urlNavigate('online-sales', 'home'));
        dispatch(doHandleState('email', ''));
        dispatch(doHandleState('data', {}));
        dispatch(doHandleState('minor', []));
    };

    const formik = useFormik({
        initialValues: {
            fullname: '',
            birthDate: '',
            phone: '',
            country: { value: 'ID', label: 'Indonesia' },
            city: '',
            address: '',
        },
        onSubmit: (values) => {
            dispatch(
                doHandleState('data', {
                    ...values,
                    country: values?.country?.value ?? '',
                    city: values?.city?.value,
                    dialCode: `+${parsePhoneNumber(values?.phone ?? '', 'ID')?.countryCallingCode ?? ''}`,
                    phoneNo: `0${parsePhoneNumber(values?.phone ?? '', 'ID')?.nationalNumber ?? ''}`,
                }),
            );

            // Save to Local Storage
            localStorage.setItem('fullname', values?.fullname ?? '');
            localStorage.setItem('birthDate', values?.birthDate ?? '');
            localStorage.setItem('country', values?.country?.value ?? '');
            localStorage.setItem('city', values?.city?.value ?? '9999');
            localStorage.setItem('address', values?.address ?? '');
            localStorage.setItem('dialCode', `+${parsePhoneNumber(values?.phone ?? '', 'ID')?.countryCallingCode ?? ''}`);
            localStorage.setItem('phone', `0${parsePhoneNumber(values?.phone ?? '', 'ID')?.nationalNumber ?? ''}`);

            navigate(urlNavigate('waiver', 'children?isEdit=false&isNewUser=true'));
        },
        enableReinitialize: true,
        validationSchema: WaiverRegistration,
    });

    let today = new Date();

    // Kurangi 18 tahun
    let eighteenYearsAgo = new Date();
    eighteenYearsAgo.setFullYear(today.getFullYear() - 18);

    // Format menjadi 'YYYY-MM-DD'
    let dateMax = eighteenYearsAgo.toISOString().split('T')[0];

    const { t } = useTranslation();

    const handleChangePhone = (e = '') => {
        formik.setFieldValue('phone', e);
    };

    const options = useMemo(() => countryList().getData(), []);

    const filterColors = (inputValue) => {
        return cityOptions.filter((i) => i.label.toLowerCase().includes(inputValue.toLowerCase()));
    };

    const promiseOptions = (inputValue) =>
        new Promise((resolve) => {
            setTimeout(() => {
                resolve(filterColors(inputValue));
            }, 600);
        });

    useEffect(() => {
        if (isEdit) {
            formik.setValues({
                fullname: data?.fullname ?? '',
                birthDate: data?.birthDate ?? '',
                phone: data?.phone ?? '',
                country: data?.country ?? { value: 'ID', label: 'Indonesia' },
                city: data?.city,
                address: data?.address ?? '',
            });
        } else {
            formik.handleReset();
        }
    }, [isEdit]);

    return (
        <FormLayoutProvider>
            <Formik initialValues={formik?.initialValues} onSubmit={formik.handleSubmit}>
                <Form>
                    <Flex w='full' h='auto' flexDirection='column' gap={3}>
                        <FormControl isRequired isInvalid={!!formik?.errors.fullname && formik?.touched.fullname}>
                            <FormLabel htmlFor='fullName'>{t('full_name')}</FormLabel>
                            <Input id='fullname' name='fullname' type='text' onChange={formik.handleChange} value={formik?.values?.fullname ?? ''} />
                            <FormErrorMessage>{formik?.errors.fullname}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!formik?.errors.phone && formik?.touched.phone}>
                            <FormLabel htmlFor='telp'>{t('telp')}</FormLabel>
                            <PhoneInput
                                international
                                style={{
                                    border: 'none',
                                    height: '45px',
                                    borderRadius: '0.375rem',
                                    borderColor: '#e2e8f0',
                                    outline: 'none',
                                }}
                                id='phone'
                                name='phone'
                                defaultCountry='ID'
                                value={formik?.values?.phone ?? ''}
                                onChange={handleChangePhone}
                            />
                            <FormErrorMessage>{formik?.errors.phone}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!formik?.errors.birthDate && formik?.touched.birthDate}>
                            <FormLabel htmlFor='birthDate'>{t('birth_date')}</FormLabel>
                            <Input id='birthDate' name='birthDate' type='date' onChange={formik.handleChange} value={formik?.values?.birthDate ?? ''} max={dateMax} />
                            <FormErrorMessage>{formik?.errors.birthDate}</FormErrorMessage>
                        </FormControl>

                        <FormControl isRequired isInvalid={!!formik?.errors.country && formik?.touched.country}>
                            <FormLabel htmlFor='country'>{t('country')}</FormLabel>
                            <Select
                                id='country'
                                name='country'
                                options={options}
                                defaultValue={{ value: 'ID', label: 'Indonesia' }}
                                onChange={(v) => {
                                    formik.setFieldValue('country', v);
                                    formik.setFieldValue('city', { city: '9999' });
                                }}
                                value={formik?.values?.country ?? ''}
                            />
                            <FormErrorMessage>{formik?.errors.country}</FormErrorMessage>
                        </FormControl>

                        {formik?.values?.country?.value === 'ID' && (
                            <FormControl isRequired={formik?.values?.country?.value === 'ID'} isInvalid={!!formik?.errors.city && formik?.touched.city}>
                                <FormLabel htmlFor='city'>{t('city')}</FormLabel>
                                <AsyncSelect cacheOptions id='city' name='city' onChange={(v) => formik.setFieldValue('city', v)} value={formik?.values?.city} loadOptions={promiseOptions} />
                                <FormErrorMessage>{formik?.errors.city}</FormErrorMessage>
                            </FormControl>
                        )}

                        {/* 
                        <FormControl
                            isRequired
                            isInvalid={!!formik?.errors.address && formik?.touched.address}
                            >
                            <FormLabel htmlFor="country">{t("address")}</FormLabel>
                            <Textarea
                                id="address"
                                name="address"
                                options={options}
                                onChange={formik.handleChange}
                                value={formik?.values?.address ?? ""}
                            />
                            <FormErrorMessage>{formik?.errors.address}</FormErrorMessage>
                        </FormControl> 
                        */}
                        <Box my={2}>
                            <Button mt={2} type='submit' colorScheme='red' width='100%'>
                                {t('next')}
                            </Button>
                            <Button mt={2} type='button' width='100%' onClick={handleBack}>
                                {t('previous')}
                            </Button>
                        </Box>
                    </Flex>
                </Form>
            </Formik>
        </FormLayoutProvider>
    );
}
